if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.onSlideChangedHandler = debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedHandler);
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.initThumbnailHeightSync();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.observeActiveMediaForHeight();
        this.syncThumbnailHeight();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.observeActiveMediaForHeight();
        this.syncThumbnailHeight();
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      initThumbnailHeightSync() {
        if (!window.ResizeObserver) return;

        this.viewerResizeObserver = new ResizeObserver(() => this.syncThumbnailHeight());
        this.activeMediaResizeObserver = new ResizeObserver(() => this.syncThumbnailHeight());
        this.viewerResizeObserver.observe(this.elements.viewer);
        this.observeActiveMediaForHeight();
        this.syncThumbnailHeight();

        this.mqlChangeHandler = () => this.syncThumbnailHeight();
        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', this.mqlChangeHandler);
        } else {
          this.mql.addListener(this.mqlChangeHandler);
        }
      }

      observeActiveMediaForHeight() {
        if (!this.activeMediaResizeObserver || !this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item');

        if (this.observedActiveMedia === activeMedia) return;
        if (this.observedActiveMedia) {
          this.activeMediaResizeObserver.unobserve(this.observedActiveMedia);
        }
        if (activeMedia) {
          this.activeMediaResizeObserver.observe(activeMedia);
        }
        this.observedActiveMedia = activeMedia;
      }

      syncThumbnailHeight() {
        if (!this.elements.thumbnails) return;
        if (!this.mql.matches) {
          this.style.removeProperty('--main-media-h');
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item');
        const mediaHeight = Math.round((activeMedia || this.elements.viewer).getBoundingClientRect().height);

        if (mediaHeight > 0) {
          this.style.setProperty('--main-media-h', `${mediaHeight}px`);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      disconnectedCallback() {
        if (this.onSlideChangedHandler && this.elements.viewer) {
          this.elements.viewer.removeEventListener('slideChanged', this.onSlideChangedHandler);
        }
        if (this.viewerResizeObserver) {
          this.viewerResizeObserver.disconnect();
        }
        if (this.activeMediaResizeObserver) {
          this.activeMediaResizeObserver.disconnect();
        }
        if (this.mqlChangeHandler) {
          if (this.mql.removeEventListener) {
            this.mql.removeEventListener('change', this.mqlChangeHandler);
          } else {
            this.mql.removeListener(this.mqlChangeHandler);
          }
        }
      }
    }
  );
}
